import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.heat";
import { MapPin, Users, Loader2, Globe, Radio, Flame, MapPinned } from "lucide-react";
import api from "@/lib/api";
import { locationLatLng, locationLabel } from "@/lib/geo";

function HeatLayer({ points }) {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    const heatPts = points.map((p) => [p.ll[0], p.ll[1], Math.max(0.6, p.count)]);
    layerRef.current = L.heatLayer(heatPts, { radius: 45, blur: 30, minOpacity: 0.4, maxZoom: 5, max: Math.max(2, ...points.map((p) => p.count)), gradient: { 0.2: "#5B7CFA", 0.5: "#E6B34A", 1.0: "#FF6B3B" } });
    layerRef.current.addTo(map);
    return () => { if (layerRef.current) map.removeLayer(layerRef.current); };
  }, [points, map]);
  return null;
}

export default function ListenerMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("all"); // all | live
  const [display, setDisplay] = useState("markers"); // markers | heat
  const [liveStation, setLiveStation] = useState(""); // station_id filter for live view

  const load = () => {
    const params = view === "live" && liveStation ? { station_id: liveStation } : {};
    return api.get("/admin/listener-locations", { params }).then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh so live pins stay current
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, liveStation]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div>;
  if (!data) return <p className="text-zinc-500 py-16 text-center">Could not load listener locations.</p>;

  const source = view === "live" ? (data.live_locations || []) : (data.locations || []);
  const points = source
    .map((loc) => ({ ...loc, ll: locationLatLng(loc), label: locationLabel(loc) }))
    .filter((p) => p.ll);
  const maxCount = points.reduce((m, p) => Math.max(m, p.count), 1);
  const radiusFor = (c) => 8 + Math.round((c / maxCount) * 26);
  const color = view === "live" ? "#34D399" : "#E6B34A";

  const Toggle = ({ active, onClick, icon: Icon, label, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div>
      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-5" data-testid="map-stat-total">
          <div className="flex items-center gap-2 text-zinc-400 text-sm"><Users className="w-4 h-4 text-[#E6B34A]" /> Total members</div>
          <p className="font-display font-black text-3xl mt-1">{data.total_users}</p>
        </div>
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-5" data-testid="map-stat-live">
          <div className="flex items-center gap-2 text-zinc-400 text-sm"><Radio className="w-4 h-4 text-emerald-400" /> Listening now</div>
          <p className="font-display font-black text-3xl mt-1 flex items-center gap-2">{data.live_count}{data.live_count > 0 && <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 live-dot" />}</p>
        </div>
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-5" data-testid="map-stat-located">
          <div className="flex items-center gap-2 text-zinc-400 text-sm"><MapPin className="w-4 h-4 text-[#E6B34A]" /> With a location</div>
          <p className="font-display font-black text-3xl mt-1">{data.located_users}</p>
        </div>
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-5" data-testid="map-stat-regions">
          <div className="flex items-center gap-2 text-zinc-400 text-sm"><Globe className="w-4 h-4 text-[#E6B34A]" /> Regions shown</div>
          <p className="font-display font-black text-3xl mt-1">{points.length}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2" data-testid="map-view-toggle">
          <Toggle active={view === "all"} onClick={() => setView("all")} icon={Users} label="All members" testid="view-all" />
          <Toggle active={view === "live"} onClick={() => setView("live")} icon={Radio} label={`Live now (${data.live_count})`} testid="view-live" />
        </div>
        <div className="flex gap-2" data-testid="map-display-toggle">
          <Toggle active={display === "markers"} onClick={() => setDisplay("markers")} icon={MapPinned} label="Pins" testid="display-markers" />
          <Toggle active={display === "heat"} onClick={() => setDisplay("heat")} icon={Flame} label="Heatmap" testid="display-heat" />
        </div>
      </div>

      {view === "live" && (data.live_stations?.length > 0) && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="live-station-filter">
          <span className="text-xs text-zinc-500 mr-1">Filter by station:</span>
          <button onClick={() => setLiveStation("")} data-testid="live-station-all" className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${liveStation === "" ? "bg-emerald-500 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
            All stations ({data.live_count})
          </button>
          {data.live_stations.map((s) => (
            <button key={s.station_id} onClick={() => setLiveStation(s.station_id)} data-testid={`live-station-${s.station_id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${liveStation === s.station_id ? "bg-emerald-500 text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
              {s.station_name} ({s.count})
            </button>
          ))}
        </div>
      )}

      <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0C1338]" data-testid="listener-map" style={{ height: 460 }}>
        {points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm px-6 text-center">
            {view === "live" ? "No one is tuned in right now. Live pins appear here the moment members start listening." : "No listener locations yet. Locations appear as members add their city, state and country."}
          </div>
        ) : (
          <MapContainer center={[30, -30]} zoom={2} scrollWheelZoom={true} style={{ height: "100%", width: "100%", background: "#0C1338" }} worldCopyJump={true}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" className="map-tiles-dark" />
            {display === "heat" ? (
              <HeatLayer points={points} />
            ) : (
              points.map((p, i) => (
                <CircleMarker key={i} center={p.ll} radius={radiusFor(p.count)} className={view === "live" ? "live-pin" : ""}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.55, weight: 1.5 }}>
                  <Tooltip direction="top">
                    <div style={{ minWidth: 120 }}>
                      <strong>{p.label}</strong><br />
                      {p.count} {view === "live" ? "listening now" : `listener${p.count > 1 ? "s" : ""}`}
                      {p.top_cities?.length > 0 && <><br /><span style={{ opacity: 0.7 }}>{p.top_cities.join(", ")}</span></>}
                    </div>
                  </Tooltip>
                </CircleMarker>
              ))
            )}
          </MapContainer>
        )}
      </div>

      {points.length > 0 && display === "markers" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="location-list">
          {points.map((p, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#141C42] px-4 py-3">
              <MapPin className="w-4 h-4 shrink-0" style={{ color }} />
              <span className="text-sm font-semibold truncate flex-1">{p.label}</span>
              <span className="text-sm font-bold tabular-nums" style={{ color }}>{p.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
